import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiMessageCircle, FiX, FiSend, FiUser, FiCpu, 
  FiMinimize2, FiMaximize2, FiRefreshCw
} from 'react-icons/fi';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  actions?: Array<{ label: string; action: () => void; icon?: string }>;
}

interface ChatbotProps {
  isOpen: boolean;
  onToggle: () => void;
  onBooksClick?: () => void;
  onVideoLearningClick?: () => void;
  onBlogsClick?: () => void;
  onMkAcademyOnlineClick?: () => void;
  onMkAcademyInPersonClick?: () => void;
}

const CSS_VARS = {
  primary: '#253C8C',
  primaryLight: '#3B4F9E',
  secondary: '#FF6B35',
  accent: '#4ECDC4',
  accentLight: '#6FDDD5',
  text: '#2C3E50',
  textLight: '#7F8C8D',
  white: '#FFFFFF',
  lightGray: '#F8F9FA',
  borderColor: '#E9ECEF',
  gradientPrimary: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  gradientSecondary: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
  gradientActive: 'linear-gradient(135deg, #253C8C 0%, #3B4F9E 100%)',
  shadow: '0 2px 10px rgba(0,0,0,0.1)',
  shadowHover: '0 4px 20px rgba(37, 60, 140, 0.15)',
  shadowActive: '0 2px 8px rgba(37, 60, 140, 0.2)',
  borderRadius: '8px',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  transitionFast: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)'
};

const ChatbotButton = styled(motion.button)`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 70px;
  height: 70px;
  border-radius: 50%;
  background: ${CSS_VARS.gradientActive};
  border: 3px solid ${CSS_VARS.white};
  color: ${CSS_VARS.white};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 32px rgba(37, 60, 140, 0.4);
  z-index: 1001;
  transition: ${CSS_VARS.transition};
  
  /* Pulse animation to make it more noticeable */
  &::before {
    content: '';
    position: absolute;
    top: -4px;
    left: -4px;
    right: -4px;
    bottom: -4px;
    border-radius: 50%;
    background: ${CSS_VARS.gradientActive};
    opacity: 0.3;
    animation: pulse 2s infinite;
    z-index: -1;
  }
  
  @keyframes pulse {
    0% {
      transform: scale(1);
      opacity: 0.3;
    }
    50% {
      transform: scale(1.1);
      opacity: 0.1;
    }
    100% {
      transform: scale(1.2);
      opacity: 0;
    }
  }
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 12px 40px rgba(37, 60, 140, 0.5);
    
    &::before {
      animation-play-state: paused;
    }
  }
  
  svg {
    width: 28px;
    height: 28px;
    filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2));
  }
`;

const ChatContainer = styled(motion.div)`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 380px;
  height: 500px;
  background: ${CSS_VARS.white};
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid ${CSS_VARS.borderColor};
  
  @media (max-width: 480px) {
    width: calc(100vw - 40px);
    height: 70vh;
    bottom: 20px;
    right: 20px;
    left: 20px;
  }
`;

const ChatHeader = styled.div`
  background: ${CSS_VARS.gradientActive};
  color: ${CSS_VARS.white};
  padding: 16px 20px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 16px 16px 0 0;
`;

const ChatHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ChatHeaderTitle = styled.div`
  h3 {
    margin: 0;
    font-size: 1.1rem;
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 0.8rem;
    opacity: 0.9;
  }
`;

const ChatHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const HeaderButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: ${CSS_VARS.white};
  padding: 6px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${CSS_VARS.transitionFast};
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const ChatMessages = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
  
  &::-webkit-scrollbar {
    width: 4px;
  }
  
  &::-webkit-scrollbar-track {
    background: ${CSS_VARS.lightGray};
  }
  
  &::-webkit-scrollbar-thumb {
    background: ${CSS_VARS.borderColor};
    border-radius: 2px;
  }
`;

const MessageBubble = styled(motion.div)<{ $isUser?: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  ${props => props.$isUser && 'flex-direction: row-reverse;'}
`;

const MessageAvatar = styled.div<{ $isUser?: boolean }>`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${props => props.$isUser ? CSS_VARS.gradientActive : CSS_VARS.accent};
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${CSS_VARS.white};
  flex-shrink: 0;
  
  svg {
    width: 18px;
    height: 18px;
  }
`;

const MessageContent = styled.div<{ $isUser?: boolean }>`
  max-width: 260px;
  padding: 12px 16px;
  border-radius: 18px;
  background: ${props => props.$isUser ? CSS_VARS.gradientActive : CSS_VARS.lightGray};
  color: ${props => props.$isUser ? CSS_VARS.white : CSS_VARS.text};
  font-size: 0.9rem;
  line-height: 1.4;
  word-wrap: break-word;
  
  ${props => props.$isUser && `
    border-bottom-right-radius: 6px;
  `}
  
  ${props => !props.$isUser && `
    border-bottom-left-radius: 6px;
  `}
`;

const MessageTime = styled.div<{ $isUser?: boolean }>`
  font-size: 0.7rem;
  color: ${CSS_VARS.textLight};
  margin-top: 4px;
  text-align: ${props => props.$isUser ? 'right' : 'left'};
`;

const ChatInput = styled.div`
  padding: 16px 20px;
  border-top: 1px solid ${CSS_VARS.borderColor};
  background: ${CSS_VARS.white};
`;

const InputContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: ${CSS_VARS.lightGray};
  border-radius: 24px;
  padding: 8px 16px;
  border: 2px solid transparent;
  transition: ${CSS_VARS.transitionFast};
  
  &:focus-within {
    border-color: ${CSS_VARS.primary};
    background: ${CSS_VARS.white};
  }
`;

const MessageInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: 8px 0;
  font-size: 0.9rem;
  color: ${CSS_VARS.text};
  outline: none;
  
  &::placeholder {
    color: ${CSS_VARS.textLight};
  }
`;

const SendButton = styled.button`
  background: ${CSS_VARS.gradientActive};
  border: none;
  color: ${CSS_VARS.white};
  padding: 8px;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${CSS_VARS.transitionFast};
  
  &:hover:not(:disabled) {
    transform: scale(1.1);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  
  svg {
    width: 16px;
    height: 16px;
  }
`;

const TypingIndicator = styled(motion.div)`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 0 4px;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  
  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${CSS_VARS.textLight};
    animation: typing 1.4s infinite ease-in-out;
    
    &:nth-child(1) { animation-delay: -0.32s; }
    &:nth-child(2) { animation-delay: -0.16s; }
  }
  
  @keyframes typing {
    0%, 80%, 100% {
      transform: scale(0.8);
      opacity: 0.5;
    }
    40% {
      transform: scale(1);
      opacity: 1;
    }
  }
`;

const WelcomeMessage = styled.div`
  text-align: center;
  padding: 40px 20px;
  color: ${CSS_VARS.textLight};
  
  .icon {
    font-size: 3rem;
    margin-bottom: 16px;
    color: ${CSS_VARS.accent};
  }
  
  h3 {
    margin: 0 0 8px 0;
    color: ${CSS_VARS.text};
    font-weight: 600;
  }
  
  p {
    margin: 0;
    font-size: 0.9rem;
    line-height: 1.4;
  }
`;

const QuickActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-top: 20px;
`;

const QuickActionButton = styled.button`
  background: ${CSS_VARS.lightGray};
  border: 1px solid ${CSS_VARS.borderColor};
  padding: 12px 16px;
  border-radius: 12px;
  color: ${CSS_VARS.text};
  cursor: pointer;
  transition: ${CSS_VARS.transitionFast};
  text-align: left;
  font-size: 0.9rem;
  
  &:hover {
    background: ${CSS_VARS.white};
    border-color: ${CSS_VARS.primary};
    color: ${CSS_VARS.primary};
  }
`;

const NavigationButton = styled.button`
  background: ${CSS_VARS.gradientActive};
  color: ${CSS_VARS.white};
  border: none;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  cursor: pointer;
  transition: ${CSS_VARS.transitionFast};
  margin: 8px 4px 4px 0;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  
  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(37, 60, 140, 0.3);
  }
  
  svg {
    width: 14px;
    height: 14px;
  }
`;

const Chatbot: React.FC<ChatbotProps> = ({ 
  isOpen, 
  onToggle,
  onBooksClick,
  onVideoLearningClick,
  onBlogsClick,
  onMkAcademyOnlineClick,
  onMkAcademyInPersonClick
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [hasAutoOpened, setHasAutoOpened] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Simple greeting when user opens chatbot
  useEffect(() => {
    if (!hasAutoOpened && isOpen) {
      setHasAutoOpened(true);
      
      // Add simple greeting when chatbot is first opened
      const greetingMessage: Message = {
        id: 'greeting-' + Date.now().toString(),
        text: "Hi! 👋 How can I help you today?",
        sender: 'bot',
        timestamp: new Date()
      };
      setMessages([greetingMessage]);
    }
  }, [isOpen, hasAutoOpened]);


  const handleSendMessage = async (text?: string) => {
    const messageText = text || inputValue.trim();
    if (!messageText) return;

    const newMessage: Message = {
      id: Date.now().toString(),
      text: messageText,
      sender: 'user',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, newMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate bot response
    setTimeout(() => {
      const response = getBotResponse(messageText);
      const botResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: response.text,
        sender: 'bot',
        timestamp: new Date(),
        actions: response.actions
      };
      
      setMessages(prev => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000 + Math.random() * 2000); // Random delay between 1-3 seconds
  };

  const getBotResponse = (userMessage: string): { text: string; actions?: Array<{ label: string; action: () => void; icon?: string }> } => {
    const message = userMessage.toLowerCase();
    
    if (message.includes('hello') || message.includes('hi') || message.includes('hey')) {
      return {
        text: "Hello! 👋 Welcome to MathKnots! I'm here to help you with any questions about our books, courses, or learning materials. How can I assist you today?"
      };
    }
    
    if (message.includes('book') || message.includes('books')) {
      return {
        text: "Great question about our books! 📚 MathKnots offers a comprehensive collection of educational books across various subjects and grade levels. You can browse our complete book catalog to find materials suitable for your learning needs.",
        actions: onBooksClick ? [{ label: "Browse Books", action: onBooksClick, icon: "📚" }] : undefined
      };
    }
    
    if (message.includes('course') || message.includes('class') || message.includes('academy')) {
      return {
        text: "Excellent! 🎓 MK Academy offers both online and in-person classes. Our courses are designed to complement our books and provide interactive learning experiences.",
        actions: [
          ...(onMkAcademyOnlineClick ? [{ label: "Online Classes", action: onMkAcademyOnlineClick, icon: "💻" }] : []),
          ...(onMkAcademyInPersonClick ? [{ label: "In-Person Classes", action: onMkAcademyInPersonClick, icon: "🏫" }] : [])
        ]
      };
    }
    
    if (message.includes('video') || message.includes('learning hub')) {
      return {
        text: "Perfect! 🎥 Our Self-Learning Hub contains engaging video lessons that align with our book content. It's a great way to reinforce your learning with visual explanations.",
        actions: onVideoLearningClick ? [{ label: "Visit Learning Hub", action: onVideoLearningClick, icon: "🎥" }] : undefined
      };
    }
    
    if (message.includes('blog') || message.includes('blogs')) {
      return {
        text: "Great! 📝 Our blog contains helpful articles, learning tips, and educational insights to support your learning journey.",
        actions: onBlogsClick ? [{ label: "Read Blogs", action: onBlogsClick, icon: "📝" }] : undefined
      };
    }
    
    if (message.includes('price') || message.includes('cost') || message.includes('fee')) {
      return {
        text: "For pricing information, I'd recommend checking our specific course or book pages, as prices vary by content type and level. You can also contact us directly at mathknots.help@gmail.com for detailed pricing information. 💰",
        actions: onBooksClick ? [{ label: "Check Book Prices", action: onBooksClick, icon: "📚" }] : undefined
      };
    }
    
    if (message.includes('contact') || message.includes('help') || message.includes('support')) {
      return {
        text: "I'm happy to help! 📧 For additional support, you can reach us at mathknots.help@gmail.com. Our team is always ready to assist with any questions about our learning materials or platform."
      };
    }
    
    if (message.includes('grade') || message.includes('level')) {
      return {
        text: "We offer materials for various grade levels! 📊 Our content spans from elementary through high school, including test prep materials. You can browse our books to find content suitable for your grade level.",
        actions: onBooksClick ? [{ label: "Find Books by Grade", action: onBooksClick, icon: "🎓" }] : undefined
      };
    }
    
    // Default responses
    const defaultResponses = [
      "That's an interesting question! 🤔 Could you provide a bit more detail so I can give you the most helpful information about MathKnots?",
      "I'd love to help you with that! ✨ MathKnots offers comprehensive learning resources. Could you tell me more about what specific area you're interested in?",
      "Thanks for your question! 📝 To give you the best answer, could you let me know if you're looking for information about our books, courses, or video content?",
      "Great question! 🎯 MathKnots is here to support your learning journey. What specific aspect of our platform would you like to know more about?"
    ];
    
    return { text: defaultResponses[Math.floor(Math.random() * defaultResponses.length)] };
  };

  const handleQuickAction = (action: string) => {
    handleSendMessage(action);
  };

  const resetChat = () => {
    setMessages([]);
    setInputValue('');
    setIsTyping(false);
  };

  const formatTime = (date: Date): string => {
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <>
      <AnimatePresence>
        {isOpen && (
          <ChatContainer
            initial={{ opacity: 0, scale: 0.8, y: 20 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              y: 0,
              height: isMinimized ? 60 : 500 
            }}
            exit={{ opacity: 0, scale: 0.8, y: 20 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
          >
            <ChatHeader>
              <ChatHeaderLeft>
                <MessageAvatar>
                  <FiCpu />
                </MessageAvatar>
                <ChatHeaderTitle>
                  <h3>MathKnots Assistant</h3>
                  {!isMinimized && (
                    <p>
                      {isTyping ? 'Typing...' : 'Online • Ready to help!'}
                    </p>
                  )}
                </ChatHeaderTitle>
              </ChatHeaderLeft>
              
              <ChatHeaderActions>
                <HeaderButton onClick={() => setIsMinimized(!isMinimized)}>
                  {isMinimized ? <FiMaximize2 /> : <FiMinimize2 />}
                </HeaderButton>
                <HeaderButton onClick={resetChat}>
                  <FiRefreshCw />
                </HeaderButton>
                <HeaderButton onClick={onToggle}>
                  <FiX />
                </HeaderButton>
              </ChatHeaderActions>
            </ChatHeader>

            {!isMinimized && (
              <>
                <ChatMessages>
                  {messages.length === 0 ? (
                    <WelcomeMessage>
                      <div className="icon">🤖</div>
                      <h3>Hello! I'm your MathKnots Assistant</h3>
                      <p>I'm here to help you navigate our platform, find the right learning materials, and answer any questions you might have about our books, courses, and resources.</p>
                      
                      <QuickActions>
                        <QuickActionButton onClick={() => handleQuickAction('Tell me about your books')}>
                          📚 Tell me about your books
                        </QuickActionButton>
                        <QuickActionButton onClick={() => handleQuickAction('What courses do you offer?')}>
                          🎓 What courses do you offer?
                        </QuickActionButton>
                        <QuickActionButton onClick={() => handleQuickAction('How can I contact support?')}>
                          📧 How can I contact support?
                        </QuickActionButton>
                      </QuickActions>
                    </WelcomeMessage>
                  ) : (
                    messages.map((message) => (
                      <MessageBubble
                        key={message.id}
                        $isUser={message.sender === 'user'}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.3 }}
                      >
                        <MessageAvatar $isUser={message.sender === 'user'}>
                          {message.sender === 'user' ? <FiUser /> : <FiCpu />}
                        </MessageAvatar>
                        <div>
                          <MessageContent $isUser={message.sender === 'user'}>
                            {message.text}
                          </MessageContent>
                          {message.actions && message.actions.length > 0 && (
                            <div style={{ marginTop: '8px' }}>
                              {message.actions.map((action, index) => (
                                <NavigationButton
                                  key={index}
                                  onClick={() => {
                                    action.action();
                                    onToggle(); // Close chatbot after navigation
                                  }}
                                >
                                  {action.icon && <span>{action.icon}</span>}
                                  {action.label}
                                </NavigationButton>
                              ))}
                            </div>
                          )}
                          <MessageTime $isUser={message.sender === 'user'}>
                            {formatTime(message.timestamp)}
                          </MessageTime>
                        </div>
                      </MessageBubble>
                    ))
                  )}
                  
                  {isTyping && (
                    <TypingIndicator
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                    >
                      <MessageAvatar>
                        <FiCpu />
                      </MessageAvatar>
                      <MessageContent>
                        <TypingDots>
                          <span />
                          <span />
                          <span />
                        </TypingDots>
                      </MessageContent>
                    </TypingIndicator>
                  )}
                  
                  <div ref={messagesEndRef} />
                </ChatMessages>

                <ChatInput>
                  <InputContainer>
                    <MessageInput
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="Type your message..."
                      onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                      disabled={isTyping}
                    />
                    <SendButton 
                      onClick={() => handleSendMessage()}
                      disabled={!inputValue.trim() || isTyping}
                    >
                      <FiSend />
                    </SendButton>
                  </InputContainer>
                </ChatInput>
              </>
            )}
          </ChatContainer>
        )}
      </AnimatePresence>

      <ChatbotButton
        onClick={onToggle}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        animate={{ 
          rotate: isOpen ? 180 : 0,
          backgroundColor: isOpen ? '#ef4444' : undefined
        }}
      >
        {isOpen ? <FiX /> : <FiMessageCircle />}
      </ChatbotButton>
    </>
  );
};

export default Chatbot;
